import axios from "axios";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "ef_token";

export const authApi = axios.create({ baseURL: API });

authApi.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

authApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error?.response?.status === 401) {
      const wasLoggedIn = !!localStorage.getItem(TOKEN_KEY);
      const url = error.config?.url || "";
      // Don't hijack the /auth/login failure itself
      if (wasLoggedIn && !url.includes("/auth/login")) {
        localStorage.removeItem(TOKEN_KEY);
        if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin") && window.location.pathname !== "/admin/login") {
          window.location.assign("/admin/login");
        }
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = auth
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await authApi.get("/auth/me");
        setUser(data);
      } catch (e) {
        localStorage.removeItem(TOKEN_KEY);
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function login(email, password) {
    const { data } = await authApi.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setUser({ email: data.email, name: data.name, role: data.role });
    return data;
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    setUser(false);
  }

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
